<template>
  <div class="page flex-col">
    <AppHeader />

    <!-- Banner -->
    <div class="pl-banner">
      <div class="pl-banner-mask">
        <span class="pl-banner-zh">{{ sectionDisplayName }}</span>
        <span v-if="!isEnglish" class="pl-banner-en">{{ activeSection.nameEn }}</span>
        <span v-else class="pl-banner-en">{{ activeSection.titleEn || 'PRODUCTS' }}</span>
      </div>
    </div>

    <div class="pl-main">
      <!-- 面包屑：列表页到分类为止，产品名仅在详情页显示 -->
      <nav class="pl-breadcrumb">
        <router-link :to="homeLink">{{ isEnglish ? 'Home' : '首页' }}</router-link>
        <span class="pl-sep">&gt;</span>
        <span>{{ sectionDisplayName }}</span>
        <template v-if="categoryDisplayLabel">
          <span class="pl-sep">&gt;</span>
          <span class="pl-crumb-active">{{ categoryDisplayLabel }}</span>
        </template>
      </nav>

      <!-- 标题 -->
      <div class="pl-title-row">
        <span class="pl-title-zh">{{ sectionDisplayName }}</span>
        <span class="pl-title-en">{{ activeSection.titleEn || 'PRODUCTS' }}</span>
      </div>

      <!-- 主体：侧栏 + 内容 -->
      <div class="pl-body">
        <aside v-if="categories.length" class="pl-sidebar">
          <button
            v-for="(cat, index) in categories"
            :key="cat.id || 'cat_' + index"
            type="button"
            class="pl-side-item"
            :class="{ active: activeCategoryIndex === index }"
            @click="setCategory(cat)"
          >
            {{ categoryDisplayName(cat) }}
          </button>
        </aside>

        <div class="pl-content">
          <div class="pl-grid">
            <div
              v-for="(item, index) in currentProducts"
              :key="item.id || 'prod_' + index"
              class="pl-card"
              :class="{ selected: selectedProductIndex === index }"
              @click="selectProduct(index)"
              @dblclick="goDetail(item)"
            >
              <div class="pl-card-img-wrap">
                <img class="pl-card-img" :src="item.image" :alt="productDisplayName(item)" />
              </div>
              <div class="pl-card-info">
                <div class="pl-card-name">{{ productDisplayName(item) }}</div>
                <a
                  class="pl-card-link"
                  href="javascript:;"
                  @click.stop.prevent="goDetail(item)"
                >{{ isEnglish ? 'View details' : '查看详情' }}</a>
              </div>
            </div>
          </div>
          <div v-if="!currentProducts.length" class="pl-empty">
            {{ isEnglish ? 'No products available' : '暂无产品数据' }}
          </div>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import { isEnglishRoute, withLang, langLink, siteHomeLink } from '@/utils/lang';

const defaultProductImg = require('./assets/img/FigmaDDSSlicePNG1fefd4a6866580ab21cf6d347ea4d160.png');
const defaultGunImg = require('./assets/img/FigmaDDSSlicePNGa29268579a1f66a27760df06030fc70a.png');

const CATEGORY_EN = {
  arc: 'Arc Spray ARC',
  hvaf: 'High Velocity Air Fuel HVAF',
  hvof: 'High Velocity Oxygen Fuel HVOF',
  aps: 'Plasma Spray APS',
  'arc-gun': 'Arc Spray Gun',
  'hvaf-gun': 'HVAF Spray Gun',
  'hvof-gun': 'HVOF Spray Gun',
  'aps-gun': 'APS Spray Gun',
  powder: 'Spray Powders / Wires',
  parts: 'Wear Parts',
  prep: 'Surface Preparation',
  control: 'Control & Powder Feed',
  robot: 'Automation'
};

export default {
  name: 'ProductList',
  data() {
    return {
      sections: [],
      defaultDetailPath: '/products/radio-detection',
      selectedProductIndex: 0
    };
  },
  computed: {
    isEnglish() {
      return isEnglishRoute(this.$route);
    },
    homeLink() {
      return siteHomeLink(this.$route);
    },
    activeSectionId() {
      return this.$route.query.section || 'equipment';
    },
    activeSection() {
      return (
        this.sections.find((s) => s.id === this.activeSectionId) ||
        this.sections[0] || {
          id: 'equipment',
          name: '热喷涂设备',
          nameEn: 'Thermal spraying products',
          titleEn: 'PRODUCTS',
          categories: []
        }
      );
    },
    sectionDisplayName() {
      if (this.isEnglish) {
        return this.activeSection.nameEn || this.activeSection.name;
      }
      return this.activeSection.name;
    },
    categories() {
      return this.activeSection.categories || [];
    },
    activeCategoryId() {
      return this.$route.query.cat || (this.categories[0] && this.categories[0].id) || '';
    },
    activeCategoryIndex() {
      const idx = this.categories.findIndex((c) => c.id === this.activeCategoryId);
      return idx >= 0 ? idx : 0;
    },
    activeCategory() {
      return this.categories[this.activeCategoryIndex] || {
        id: '',
        name: '',
        label: '',
        products: []
      };
    },
    categoryDisplayLabel() {
      if (!this.activeCategory || (!this.activeCategory.label && !this.activeCategory.name)) {
        return '';
      }
      return this.categoryDisplayName(this.activeCategory);
    },
    currentProducts() {
      return this.activeCategory.products || [];
    }
  },
  created() {
    this.fetchDeviceData();
  },
  watch: {
    activeSectionId() {
      this.selectedProductIndex = 0;
      this.ensureCategoryQuery();
    },
    activeCategoryId() {
      this.selectedProductIndex = 0;
    },
    categories() {
      this.ensureCategoryQuery();
    }
  },
  methods: {
    productDisplayName(item) {
      if (!item) return '';
      if (this.isEnglish) return item.nameEn || item.name || item.id || '';
      return item.name || item.id || '';
    },
    categoryDisplayName(cat) {
      if (!cat) return '';
      if (!this.isEnglish) return cat.name || cat.label || '';
      return cat.nameEn || cat.labelEn || CATEGORY_EN[cat.id] || cat.name || cat.label || '';
    },
    async fetchDeviceData() {
      try {
        const res = await fetch('/data/device.json');
        if (!res.ok) return;
        const data = await res.json();
        if (data.defaultDetailPath) {
          this.defaultDetailPath = data.defaultDetailPath;
        }
        if (Array.isArray(data.sections) && data.sections.length) {
          this.sections = data.sections.map((section) => ({
            ...section,
            categories: (section.categories || []).map((cat) => ({
              ...cat,
              products: (cat.products || []).map((item) => this.normalizeItem(item, section.id))
            }))
          }));
        }
      } catch (err) {
        console.warn('Failed to fetch device.json:', err);
      }
    },
    normalizeItem(item, sectionId) {
      const fallback = sectionId === 'guns' ? defaultGunImg : defaultProductImg;
      return {
        ...item,
        image: item.image || fallback
      };
    },
    ensureCategoryQuery() {
      if (!this.categories.length) return;
      const hasCat = this.categories.some((c) => c.id === this.$route.query.cat);
      if (!hasCat) {
        const first = this.categories[0];
        if (first && first.id) {
          this.$router.replace({
            path: this.$route.path,
            query: withLang(
              {
                ...this.$route.query,
                section: this.activeSectionId,
                cat: first.id
              },
              this.$route
            )
          });
        }
      }
    },
    setCategory(cat) {
      if (!cat || !cat.id) return;
      this.$router.push(
        langLink(
          '/products/catalog',
          {
            section: this.activeSectionId,
            cat: cat.id
          },
          this.$route
        )
      );
    },
    selectProduct(index) {
      this.selectedProductIndex = index;
    },
    /**
     * 详情跳转规则（可在 device.json 中配置）：
     * 1. item.path 存在 → 跳转专用页面（可配合 item.query）
     * 2. 否则按 section.defaultDetailPath → 全局 defaultDetailPath
     *    默认：equipment/consumables/auxiliary → lanhu_3；guns → lanhu_4
     */
    goDetail(item) {
      if (!item) return;
      const baseQuery = withLang(
        {
          id: item.id || '',
          section: this.activeSectionId,
          cat: this.activeCategory.id || ''
        },
        this.$route
      );
      if (item.path) {
        this.$router.push({
          path: item.path,
          query: withLang(
            {
              ...baseQuery,
              ...(item.query || {})
            },
            this.$route
          )
        });
        return;
      }
      if (item.id) {
        const sectionPath = this.activeSection.defaultDetailPath;
        this.$router.push({
          path: item.detailPath || sectionPath || this.defaultDetailPath,
          query: baseQuery
        });
      }
    }
  }
};
</script>

<style scoped>
.page {
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  overflow-x: hidden;
}

/* Banner */
.pl-banner {
  width: 100%;
  height: 360px;
  background: url(./assets/img/FigmaDDSSlicePNG4bfbddd95358d0de5c2a6890b148e575.jpg)
    center center / cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.pl-banner::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.28);
}

.pl-banner-mask {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.pl-banner-zh {
  color: #fff;
  font-size: 44px;
  font-weight: 900;
  line-height: 1.2;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.pl-banner-en {
  color: rgba(255, 255, 255, 0.95);
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

/* 版心 */
.pl-main {
  width: 1550px;
  max-width: 92%;
  margin: 0 auto;
  padding: 24px 0 64px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.pl-breadcrumb {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 28px;
}

.pl-breadcrumb a {
  color: #6b7280;
  text-decoration: none;
}

.pl-breadcrumb a:hover {
  color: #1e40af;
}

.pl-sep {
  color: #9ca3af;
}

.pl-crumb-active {
  color: #374151;
}

.pl-title-row {
  display: flex;
  align-items: baseline;
  gap: 14px;
  margin-bottom: 28px;
}

.pl-title-zh {
  font-size: 32px;
  font-weight: 900;
  color: #1e40af;
  line-height: 1.2;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.pl-title-en {
  font-size: 18px;
  font-weight: 700;
  color: #1e40af;
  letter-spacing: 1px;
}

.pl-body {
  width: 100%;
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 36px;
  align-items: start;
}

/* 侧栏 */
.pl-sidebar {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.pl-side-item {
  width: 100%;
  border: none;
  outline: none;
  cursor: pointer;
  text-align: left;
  padding: 18px 20px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  color: #1e40af;
  background: #dbeafe;
  transition: background 0.2s ease, color 0.2s ease;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.pl-side-item:hover {
  background: #bfdbfe;
}

.pl-side-item.active {
  background: #1e40af;
  color: #fff;
}

/* 内容区 */
.pl-content {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

/* 产品网格 */
.pl-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.pl-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
}

.pl-card:hover {
  border-color: #93c5fd;
  box-shadow: 0 6px 18px rgba(30, 64, 175, 0.08);
}

.pl-card.selected {
  border-color: #1e40af;
  border-width: 1.5px;
}

.pl-card-img-wrap {
  width: 100%;
  aspect-ratio: 1 / 0.92;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
}

.pl-card-img {
  max-width: 88%;
  max-height: 88%;
  object-fit: contain;
}

.pl-card-info {
  padding: 16px 18px 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.pl-card-name {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}

.pl-card-link {
  font-size: 14px;
  color: #1e40af;
  text-decoration: none;
  font-weight: 500;
}

.pl-card-link:hover {
  text-decoration: underline;
}

.pl-empty {
  width: 100%;
  padding: 48px 0;
  text-align: center;
  color: #9ca3af;
  font-size: 15px;
}

@media (max-width: 1100px) {
  .pl-body {
    grid-template-columns: 1fr;
  }

  .pl-sidebar {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .pl-side-item {
    width: auto;
    flex: 1 1 200px;
  }

  .pl-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 700px) {
  .pl-grid {
    grid-template-columns: 1fr;
  }

  .pl-banner {
    height: 240px;
  }

  .pl-banner-zh {
    font-size: 32px;
  }
}
</style>
